import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import Header from '../components/Header';

const AgentConfigPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [assistantLoading, setAssistantLoading] = useState(false);
  
  // Estados dos dados
  const [businessData, setBusinessData] = useState({
    website: '',
    instagram: '',
    facebook: ''
  });
  
  const [discoveryResult, setDiscoveryResult] = useState(null);
  const [clientConsiderations, setClientConsiderations] = useState('');
  const [assistantPrompt, setAssistantPrompt] = useState('');
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  
  // Estados de UI
  const [activeTab, setActiveTab] = useState('discovery');
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadExistingData();
  }, [user]);

  const loadExistingData = async () => {
    if (!user?.id) {
      console.log('User ID não disponível:', user);
      return;
    }
    
    console.log('Carregando dados para usuário:', user.id);
    
    try {
      setLoading(true);
      
      // Carregar dados do usuário do backend
      const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}`);
      if (userResponse.ok) {
        const userData = await userResponse.json();
        if (userData.success) {
          setBusinessData({
            website: userData.user.website || '',
            instagram: userData.user.instagram || '',
            facebook: userData.user.facebook || ''
          });
          console.log('Dados do usuário carregados:', userData.user);
          
          // Mostrar mensagem se dados foram encontrados
          if (userData.user.website || userData.user.instagram || userData.user.facebook) {
            showMessage('success', 'Dados do negócio carregados com sucesso!');
          }
        }
      } else {
        console.error('Erro ao carregar dados do usuário:', userResponse.status);
        showMessage('error', 'Erro ao carregar dados do usuário');
      }
      
      // Carregar Discovery existente
      const discoveryResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/discovery`);
      if (discoveryResponse.ok) {
        const discoveryData = await discoveryResponse.json();
        if (discoveryData.success) {
          setDiscoveryResult(discoveryData.discovery.discoveryResult);
          setKnowledgeBase(discoveryData.discovery.discoveryResult?.products_services || []);
          console.log('Discovery encontrado:', discoveryData.discovery);
        }
      } else if (discoveryResponse.status === 404) {
        console.log('Nenhum Discovery encontrado para este usuário');
      } else {
        console.error('Erro ao carregar Discovery:', discoveryResponse.status);
      }
      
      // Carregar produtos para base de conhecimento
      const productsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/products`);
      if (productsResponse.ok) {
        const productsData = await productsResponse.json();
        if (productsData.success && productsData.products) {
          setKnowledgeBase(productsData.products);
          console.log('Produtos carregados para base de conhecimento:', productsData.products.length);
        }
      } else if (productsResponse.status === 404) {
        console.log('Nenhum produto encontrado para este usuário');
        setKnowledgeBase([]);
      } else {
        console.error('Erro ao carregar produtos:', productsResponse.status);
      }

      // Carregar Assistente existente
      const assistantResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/assistant`);
      if (assistantResponse.ok) {
        const assistantData = await assistantResponse.json();
        if (assistantData.success) {
          setAssistantPrompt(assistantData.assistant.prompt);
          setClientConsiderations(assistantData.assistant.clientConsiderations || '');
          console.log('Assistente encontrado:', assistantData.assistant);
        }
      } else if (assistantResponse.status === 404) {
        console.log('Nenhum Assistente encontrado para este usuário');
      } else {
        console.error('Erro ao carregar Assistente:', assistantResponse.status);
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      showMessage('error', 'Erro ao carregar dados existentes');
    } finally {
      setLoading(false);
    }
  };

  const runDiscovery = async () => {
    if (!businessData.website) {
      showMessage('error', 'Website é obrigatório para executar o Discovery');
      return;
    }
    
    try {
      setDiscoveryLoading(true);
      showMessage('info', 'Iniciando Discovery... Isso pode levar alguns minutos.');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/discovery/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          website: businessData.website,
          socialMedia: {
            instagram: businessData.instagram,
            facebook: businessData.facebook
          }
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showMessage('info', 'Discovery iniciado! Verificando progresso...');
        
        // Verificar status periodicamente
        const checkStatus = async () => {
          try {
            const statusResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/discovery/status`);
            const statusData = await statusResponse.json();
            
            if (statusData.success) {
              if (statusData.status === 'completed') {
                // Carregar resultado do Discovery
                const discoveryResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/discovery`);
                if (discoveryResponse.ok) {
                  const discoveryData = await discoveryResponse.json();
                  if (discoveryData.success) {
                    setDiscoveryResult(discoveryData.discovery.discoveryResult);
                    setKnowledgeBase(discoveryData.discovery.discoveryResult?.products_services || []);
                    showMessage('success', 'Discovery executado com sucesso!');
                    setActiveTab('results');
                    setDiscoveryLoading(false);
                    return;
                  }
                }
              } else if (statusData.status === 'failed') {
                showMessage('error', `Discovery falhou: ${statusData.error || 'Erro desconhecido'}`);
                setDiscoveryLoading(false);
                return;
              } else if (statusData.status === 'running') {
                // Continuar verificando
                setTimeout(checkStatus, 5000);
                return;
              }
            }
            
            // Se chegou aqui, algo deu errado
            showMessage('error', 'Erro ao verificar status do Discovery');
            setDiscoveryLoading(false);
            
          } catch (error) {
            console.error('Erro ao verificar status:', error);
            showMessage('error', 'Erro ao verificar status do Discovery');
            setDiscoveryLoading(false);
          }
        };
        
        // Iniciar verificação após 3 segundos
        setTimeout(checkStatus, 3000);
        
      } else {
        showMessage('error', data.error || 'Erro ao executar Discovery');
        setDiscoveryLoading(false);
      }
      
    } catch (error) {
      console.error('Erro no Discovery:', error);
      showMessage('error', 'Erro ao executar Discovery');
      setDiscoveryLoading(false);
    }
  };

  const generateAssistant = async () => {
    if (!discoveryResult) {
      showMessage('error', 'Execute o Discovery primeiro antes de gerar o assistente');
      return;
    }
    
    try {
      setAssistantLoading(true);
      showMessage('info', 'Gerando assistente personalizado...');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/assistant/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientConsiderations: clientConsiderations
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAssistantPrompt(data.assistantPrompt);
        showMessage('success', 'Assistente criado com sucesso!');
        setActiveTab('assistant');
      } else {
        showMessage('error', data.error || 'Erro ao gerar assistente');
      }
      
    } catch (error) {
      console.error('Erro ao gerar assistente:', error);
      showMessage('error', 'Erro ao gerar assistente');
    } finally {
      setAssistantLoading(false);
    }
  };

  const updateAssistant = async () => {
    try {
      setAssistantLoading(true);
      showMessage('info', 'Atualizando assistente com novos produtos...');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/assistant/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        showMessage('success', `Assistente atualizado! ${data.productsCount} produtos na base de conhecimento.`);
        setKnowledgeBase(Array(data.productsCount).fill({})); // Atualizar contador
      } else {
        showMessage('error', data.error || 'Erro ao atualizar assistente');
      }
    } catch (error) {
      console.error('Erro ao atualizar assistente:', error);
      showMessage('error', 'Erro ao atualizar assistente');
    } finally {
      setAssistantLoading(false);
    }
  };

  const deleteAssistant = async () => {
    if (!window.confirm('Tem certeza que deseja deletar o assistente? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      setAssistantLoading(true);
      showMessage('info', 'Deletando assistente...');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/assistant`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        showMessage('success', 'Assistente deletado com sucesso!');
        setAssistantPrompt('');
        setKnowledgeBase([]);
      } else {
        showMessage('error', data.error || 'Erro ao deletar assistente');
      }
    } catch (error) {
      console.error('Erro ao deletar assistente:', error);
      showMessage('error', 'Erro ao deletar assistente');
    } finally {
      setAssistantLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const formatDiscoveryText = (discoveryResult) => {
    if (!discoveryResult) return 'Nenhum Discovery executado ainda.';
    
    const profile = discoveryResult.business_profile || {};
    const analysis = discoveryResult.content_analysis || {};
    const recommendations = discoveryResult.recommendations || {};
    
    return `
PERFIL DO NEGÓCIO:
• Nome: ${profile.business_name || 'Não identificado'}
• Tipo: ${profile.business_type === 'products' ? 'Produtos' : 'Serviços'}
• Setor: ${profile.industry || 'Não identificado'}
• Público-alvo: ${profile.target_audience || 'Não identificado'}

COMUNICAÇÃO:
• Tom: ${profile.communication_tone || 'Não identificado'}
• Personalidade: ${profile.brand_personality || 'Não identificado'}
• Estilo: ${profile.language_style || 'Não identificado'}

PROPOSTA DE VALOR:
${profile.value_proposition || 'Não identificado'}

DIFERENCIAIS:
${profile.differentiators?.join(', ') || 'Não identificado'}

ANÁLISE DE CONTEÚDO:
• Palavras-chave: ${analysis.main_keywords?.join(', ') || 'Não identificado'}
• Temas: ${analysis.content_themes?.join(', ') || 'Não identificado'}
• Estilo de engajamento: ${analysis.engagement_style || 'Não identificado'}

RECOMENDAÇÕES:
• Personalidade do assistente: ${recommendations.assistant_personality || 'Não identificado'}
• Diretrizes: ${recommendations.communication_guidelines?.join(', ') || 'Não identificado'}
    `.trim();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Configuração do Agente</h1>
          <p className="text-gray-600">Configure e personalize seu assistente de IA para atendimento ao cliente</p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
            'bg-blue-50 text-blue-800 border border-blue-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { id: 'discovery', name: 'Discovery Setup', icon: '🔍' },
                { id: 'results', name: 'Discovery Results', icon: '📊' },
                { id: 'assistant', name: 'Assistant Config', icon: '🤖' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Discovery Setup Tab */}
            {activeTab === 'discovery' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Configuração do Discovery</h2>
                  <p className="text-gray-600 mb-6">
                    Forneça as informações do seu negócio para que nossa IA possa analisar e criar um perfil completo.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website *
                    </label>
                    <input
                      type="url"
                      value={businessData.website}
                      onChange={(e) => setBusinessData({...businessData, website: e.target.value})}
                      placeholder="https://meusite.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Instagram (opcional)
                    </label>
                    <input
                      type="text"
                      value={businessData.instagram}
                      onChange={(e) => setBusinessData({...businessData, instagram: e.target.value})}
                      placeholder="@meuinstagram ou https://instagram.com/meuinstagram"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Facebook (opcional)
                    </label>
                    <input
                      type="text"
                      value={businessData.facebook}
                      onChange={(e) => setBusinessData({...businessData, facebook: e.target.value})}
                      placeholder="https://facebook.com/minhapagina"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex justify-center pt-6">
                  <button
                    onClick={runDiscovery}
                    disabled={discoveryLoading || !businessData.website}
                    className="bg-orange-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {discoveryLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Executando Discovery...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">🔍</span>
                        Run Discovery
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Discovery Results Tab */}
            {activeTab === 'results' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Resultados do Discovery</h2>
                  <p className="text-gray-600 mb-6">
                    Veja o que nossa IA descobriu sobre seu negócio e a base de conhecimento gerada.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Agent Discovery */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Agent Discovery
                    </label>
                    <textarea
                      value={formatDiscoveryText(discoveryResult)}
                      readOnly
                      rows={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                    />
                  </div>

                  {/* Knowledge Base */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Base de Conhecimento (Produtos/Serviços)
                    </label>
                    <div className="border border-gray-300 rounded-md bg-gray-50 p-4 h-96 overflow-y-auto">
                      {knowledgeBase.length > 0 ? (
                        <div className="space-y-4">
                          {knowledgeBase.map((item, index) => (
                            <div key={index} className="bg-white p-3 rounded border">
                              <div className="flex justify-between items-start">
                                <h4 className="font-medium text-gray-900">{item.name}</h4>
                                {item.price && (
                                  <span className="text-green-600 font-semibold">
                                    R$ {typeof item.price === 'number' ? item.price.toFixed(2) : item.price}
                                  </span>
                                )}
                              </div>
                              {item.description && (
                                <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                              )}
                              <div className="flex flex-wrap gap-2 mt-2">
                                {item.category && (
                                  <span className="inline-block bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">
                                    {item.category}
                                  </span>
                                )}
                                {item.source && (
                                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                    {item.source === 'product_discovery' ? 'Auto-descoberto' : item.source}
                                  </span>
                                )}
                                {item.url && (
                                  <a 
                                    href={item.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded hover:bg-gray-200"
                                  >
                                    🔗 Ver produto
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">
                          Nenhum produto ou serviço encontrado. Execute o Discovery primeiro.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Client Considerations */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Considerações do Cliente
                  </label>
                  <textarea
                    value={clientConsiderations}
                    onChange={(e) => setClientConsiderations(e.target.value)}
                    placeholder="Adicione informações específicas que você gostaria que o assistente soubesse sobre seu negócio, tom de atendimento, políticas especiais, etc."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="flex justify-center pt-6">
                  <button
                    onClick={generateAssistant}
                    disabled={assistantLoading || !discoveryResult}
                    className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {assistantLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Gerando Assistente...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">🤖</span>
                        Criar/Atualizar Assistente
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Assistant Config Tab */}
            {activeTab === 'assistant' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Configuração do Assistente</h2>
                  <p className="text-gray-600 mb-6">
                    Prompt personalizado gerado para seu assistente de IA baseado no Discovery e suas considerações.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prompt do Assistente
                  </label>
                  <textarea
                    value={assistantPrompt}
                    onChange={(e) => setAssistantPrompt(e.target.value)}
                    placeholder="O prompt do assistente será gerado automaticamente após executar o Discovery e criar o assistente."
                    rows={20}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                  />
                </div>

                {assistantPrompt && (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-green-600 mr-2">✅</span>
                          <span className="text-green-800 font-medium">Assistente OpenAI Criado!</span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={updateAssistant}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                            disabled={assistantLoading}
                          >
                            🔄 Atualizar
                          </button>
                          <button
                            onClick={deleteAssistant}
                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                            disabled={assistantLoading}
                          >
                            🗑️ Deletar
                          </button>
                        </div>
                      </div>
                      <p className="text-green-700 text-sm mt-1">
                        Assistente criado na OpenAI com base de conhecimento dos seus produtos.
                      </p>
                      {knowledgeBase.length > 0 && (
                        <p className="text-green-600 text-xs mt-1">
                          📚 Base de conhecimento: {knowledgeBase.length} produtos
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentConfigPage;

